import { useEffect, useState } from 'react';
import { loadDeferredHomeSections, loadHomePage } from './api.js';
import { markBenchmark, recordBenchmarkError } from './benchmark.js';
import BenchmarkPanel from './BenchmarkPanel.jsx';
import { APP_CONFIG, appHref } from './config.js';
import Icon from './icons.jsx';

const movieResourceHosts = new Set([
  'mvres.foxie.cc',
  'mvres-direct.foxie.cc',
  'mvres.kuaicloud.one',
]);

function categoryIcon(category) {
  const name = category.TypeName ?? '';
  if (category.TypeID === 1001) return 'home';
  if (category.TypeID === 1002) return 'bolt';
  if (category.TypeID === 1003) return 'chart';
  if (name.includes('电影')) return 'film';
  if (name.includes('动漫')) return 'play';
  if (name.includes('综艺')) return 'tv';
  return 'video';
}

function normalizeImageUrl(url, width = 480) {
  if (!url || url.startsWith('data:')) return url;

  try {
    const imageUrl = new URL(url, window.location.origin);
    if (
      APP_CONFIG.environment === 'test'
      && APP_CONFIG.networkPath === 'direct'
      && imageUrl.hostname === 'mvres.foxie.cc'
    ) {
      imageUrl.hostname = 'mvres-direct.foxie.cc';
    }

    if (movieResourceHosts.has(imageUrl.hostname)) {
      imageUrl.searchParams.set('w', width);
      imageUrl.searchParams.set('q', '78');
      imageUrl.searchParams.set('fm', 'webp');
      return imageUrl.toString();
    }
  } catch {
    return url;
  }

  return url;
}

function Logo() {
  return (
    <div className="brand" aria-label="快追影视">
      <span className="brand-mark">
        <span />
        <span />
        <span />
      </span>
      <strong>快追影视</strong>
    </div>
  );
}

function Sidebar({ categories, open, onClose }) {
  return (
    <aside className={`sidebar ${open ? 'is-open' : ''}`}>
      <div className="sidebar-top">
        <Logo />
        <button className="sidebar-close" onClick={onClose} aria-label="关闭菜单">
          <Icon name="close" size={22} />
        </button>
      </div>
      <nav className="side-nav" aria-label="影片分类">
        {categories.map((category) => (
          <a
            className={category.TypeID === 1001 ? 'active' : ''}
            href={appHref(category.TypeID === 1001 ? '/' : `/${category.TypeID}`)}
            key={category.TypeID}
          >
            <Icon name={categoryIcon(category)} size={16} />
            <span>{category.TypeName}</span>
            {category.Extra ? <small>{category.Extra}</small> : null}
          </a>
        ))}
      </nav>
      <footer className="side-footer">
        <a href="https://t.me/xiaomengkf">商务合作</a>
        <a href="https://t.me/xiaomengkf">推广赚钱</a>
        <p>© 2026 快追影视 版权所有</p>
      </footer>
    </aside>
  );
}

function Header({ categories, hotwords, onMenu }) {
  const [query, setQuery] = useState('');
  const placeholder = hotwords?.[0]
    ? `搜索电影、剧集，例如「${hotwords[0]}」`
    : '搜索电影、剧集...';

  return (
    <header className="topbar">
      <div className="mobile-top-row">
        <Logo />
        <div className="mobile-top-actions">
          <button aria-label="搜索"><Icon name="search" size={22} /></button>
          <button onClick={onMenu} aria-label="打开菜单"><Icon name="menu" size={23} /></button>
        </div>
      </div>
      <nav className="mobile-category-strip" aria-label="快捷分类">
        {categories.map((category) => (
          <a
            className={category.TypeID === 1001 ? 'active' : ''}
            href={appHref(category.TypeID === 1001 ? '/' : `/${category.TypeID}`)}
            key={category.TypeID}
          >
            {category.TypeName}
          </a>
        ))}
      </nav>
      <button className="menu-button" onClick={onMenu} aria-label="打开菜单">
        <Icon name="menu" size={24} />
      </button>
      <div className="mobile-logo"><Logo /></div>
      <form className="search" onSubmit={(event) => event.preventDefault()}>
        <Icon name="search" size={18} />
        <input
          aria-label="Search movies"
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          value={query}
        />
        <button type="submit">搜索</button>
      </form>
      <div className="header-actions">
        <button className="history-button">
          <Icon name="clock" size={25} />
          <span>历史记录</span>
        </button>
        <button className="login-button">登录</button>
        <button className="download-button">下载APP</button>
      </div>
    </header>
  );
}

function Hero({ banners }) {
  const [active, setActive] = useState(0);
  const items = banners?.length ? banners : [];
  const banner = items[active] ?? null;

  useEffect(() => {
    if (items.length < 2) return undefined;
    const timer = window.setInterval(
      () => setActive((value) => (value + 1) % items.length),
      6000,
    );
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (!banner) return <HeroSkeleton />;

  const description = [banner.VodClass, banner.VodActor]
    .filter(Boolean)
    .join(' | ');

  return (
    <section className="hero" aria-label="精选推荐">
      <img
        alt={banner.VodName || 'Banner slide'}
        className="hero-image"
        decoding="async"
        fetchPriority="high"
        height="900"
        src={normalizeImageUrl(banner.BannerUrl || banner.BannerUrlOrigin, 1920)}
        onLoad={() => markBenchmark('hero_image_loaded')}
        width="1920"
      />
      <div className="hero-shade" />
      <div className="hero-copy">
        <h1>{banner.VodName || '精彩影片'}</h1>
        <p>{description || '精彩剧情即将上演，跌宕起伏的故事等你来探索发现'}</p>
        <button className="play-button">
          <span className="play-triangle" />
          立即播放
        </button>
      </div>
      {items.length > 1 ? (
        <div className="hero-dots" aria-label="轮播位置">
          {items.map((item, index) => (
            <button
              aria-label={`显示 ${item.VodName || `第 ${index + 1} 张`}`}
              className={index === active ? 'active' : ''}
              key={`${item.VodID ?? index}-${index}`}
              onClick={() => setActive(index)}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}

function HeroSkeleton() {
  return (
    <section className="hero hero-skeleton" aria-label="内容加载中">
      <div className="hero-copy">
        <span className="skeleton skeleton-title" />
        <span className="skeleton skeleton-line" />
        <span className="skeleton skeleton-button" />
      </div>
    </section>
  );
}

function PosterCard({ item, index }) {
  const score = item.VodDoubanScore?.trim();
  return (
    <a className="poster-card" href={appHref(`/detail/${item.VodID}`)}>
      <div className="poster-image-wrap">
        <img
          alt={item.VodName}
          decoding="async"
          height="450"
          loading={index < 6 ? 'eager' : 'lazy'}
          src={normalizeImageUrl(item.VodPicThumb || item.LocalVodPic, 320)}
          width="320"
        />
        <div className="poster-gradient" />
        {score ? <span className="score">{score}</span> : null}
        {item.VodRemarks ? <span className="remarks">{item.VodRemarks}</span> : null}
      </div>
      <h3>{item.VodName}</h3>
    </a>
  );
}

function VodSection({ section, first }) {
  return (
    <section className="vod-section">
      <div className="section-heading">
        <h2>{section.Title}</h2>
        {section.MoreBtn ? (
          <button aria-label={`查看更多${section.Title}`}>
            <span>更多</span>
            <Icon name="chevron" size={23} />
          </button>
        ) : null}
      </div>
      <div className="poster-grid">
        {(section.Items ?? []).map((item, index) => (
          <PosterCard
            index={first ? index : index + 6}
            item={item}
            key={`${item.DataSource || 'vod'}-${item.VodID}-${index}`}
          />
        ))}
      </div>
    </section>
  );
}

function ContentSkeleton() {
  return (
    <section className="vod-section loading-section">
      <div className="section-heading"><span className="skeleton section-title-skeleton" /></div>
      <div className="poster-grid">
        {Array.from({ length: 6 }, (_, index) => (
          <div className="poster-card" key={index}>
            <span className="skeleton poster-skeleton" />
            <span className="skeleton poster-name-skeleton" />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function App() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    let active = true;
    let firstPaintFrame;
    let afterPaintFrame;

    loadHomePage()
      .then((result) => {
        if (!active) return;
        markBenchmark('home_data_received', { sectionCount: result.sections.length });
        setData(result);

        if (APP_CONFIG.supportsSectionRange) {
          firstPaintFrame = window.requestAnimationFrame(() => {
            afterPaintFrame = window.requestAnimationFrame(() => {
              if (!active) return;
              markBenchmark('deferred_request_started');
            loadDeferredHomeSections()
              .then((deferredSections) => {
                if (!active || deferredSections.length === 0) return;
                markBenchmark('deferred_sections_received', {
                  sectionCount: deferredSections.length,
                });
                setData((current) => {
                  if (!current) return current;
                  const existingSectionIDs = new Set(
                    current.sections.map(
                      (section) => section.ParentTypeID || section.SubCategory,
                    ),
                  );
                  const newSections = deferredSections.filter(
                    (section) => !existingSectionIDs.has(
                      section.ParentTypeID || section.SubCategory,
                    ),
                  );
                  return {
                    ...current,
                    sections: [...current.sections, ...newSections],
                  };
                });
              })
              .catch((reason) => recordBenchmarkError('deferred_sections', reason));
            });
          });
        }
      })
      .catch((reason) => {
        recordBenchmarkError('home_page', reason);
        if (active) setError(reason instanceof Error ? reason.message : String(reason));
      });
    return () => {
      active = false;
      window.cancelAnimationFrame(firstPaintFrame);
      window.cancelAnimationFrame(afterPaintFrame);
    };
  }, []);

  useEffect(() => {
    const sectionCount = data?.sections?.length ?? 0;
    if (sectionCount === 0) return;
    markBenchmark('first_sections_rendered', { sectionCount });
    if (!APP_CONFIG.supportsSectionRange || sectionCount > APP_CONFIG.initialSectionCount) {
      markBenchmark('all_sections_rendered', { sectionCount });
    }
  }, [data?.sections?.length]);

  const categories = data?.categories?.length ? data.categories : [];

  return (
    <div className="app-shell">
      <Sidebar categories={categories} open={menuOpen} onClose={() => setMenuOpen(false)} />
      {menuOpen ? <button className="scrim" onClick={() => setMenuOpen(false)} aria-label="关闭菜单" /> : null}
      <div className="page">
        <Header categories={categories} hotwords={data?.hotwords} onMenu={() => setMenuOpen(true)} />
        <main>
          <Hero banners={data?.detail?.BannerItems} />
          <div className="content">
            {error ? (
              <div className="error-state">
                <strong>首页加载失败</strong>
                <span>{error}</span>
                <button onClick={() => window.location.reload()}>重新加载</button>
              </div>
            ) : data ? (
              data.sections.map((section, index) => (
                <VodSection
                  first={index === 0}
                  key={`${section.ParentTypeID || section.SubCategory || section.Title}-${index}`}
                  section={section}
                />
              ))
            ) : (
              <ContentSkeleton />
            )}
          </div>
        </main>
      </div>
      <BenchmarkPanel />
    </div>
  );
}
